import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { RegisterBank } from "src/app/core/models/iugu-model";
import { MyErrorStateMatcher } from "src/app/utils/myErrorStateMatcher";
import Swal from "sweetalert2";
import { FileUploadService } from "../../../core/services/default/file-upload.service";
import { LotsRegisterService } from "../../../pages/lots-register/lots-register.service";
import {
  error as customError,
  errorHtml,
  successHtml,
} from "../../../utils/swal.model";
import { BANK_ACCOUNT_LENGTHS } from './bank-lengths';
import { BANK_MASKS } from './bank-masks';
import { listCodeBank } from "./listCodeBank";
import { ModalData } from "./modal-register.model";

@Component({
  selector: "app-modal-register-bank",
  templateUrl: "./modal-register-bank.component.html",
  styleUrls: ["./modal-register-bank.component.scss"],
})
export class ModalRegisterBankComponent implements OnInit {
  matcher = new MyErrorStateMatcher();
  formRegisterBank: FormGroup;
  formMessageCheckbox: FormGroup;
  formMessageCheckboxVisible = false;
  loading = false;
  codeBankList = listCodeBank;
  codeBankListFull = listCodeBank;
  typeUser: string = '';
  public bankAgMask: string = '0000';
  public bankCcMask: string = '00000000-0';
  inputPatterns: any = {
    'A': { pattern: new RegExp('[0-9]') }
  };
  documentsStatus = {
    selfie: null as string | null,
    identification: null as string | null
  };

  @Input() btnClose: boolean = true;
  @Input() urlBackButton: string = "/";
  @Input() bankData: RegisterBank[] = [];
  @Input() documents: { kind: string; status: string }[] = [];
  @Input() textModalSuccess = "";
  @ViewChild('uploadDocumentInput') uploadDocumentInput: ElementRef;
  @ViewChild('uploadSelfieInput') uploadSelfieInput: ElementRef;

  constructor(
    private dialogRef: MatDialogRef<ModalRegisterBankComponent>,
    private formBuilder: FormBuilder,
    private lotsRegisterService: LotsRegisterService,
    private fileUploadService: FileUploadService,
    @Inject(MAT_DIALOG_DATA) public modalData: ModalData,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.setFormGroup();
    if (this.documents && this.documents.length > 0) {
      this.documentsStatus.selfie = this.documents.find(d => d.kind === 'selfie')?.status || null;
      this.documentsStatus.identification = this.documents.find(d => d.kind === 'identification')?.status || null;
    }
    const userData = JSON.parse(localStorage.getItem("user") || '{}');
    this.typeUser = userData?.userLoginInfo?.typeUser || '';
    this.configureEstimatedRevenueValidation();
    this.setFormGroupMessageCheckbox();
    this.formRegisterBank.get('bank')?.valueChanges.subscribe((bankValue) => {
      if (!bankValue) {
        this.bankAgMask = '0000';
        this.bankCcMask = '00000000-0';
        return;
      }

      const bankCode = bankValue.split(' - ')[0];
      const mask = BANK_MASKS[bankCode];
      this.updateBankMask(bankCode);

      if (mask) {
        this.bankAgMask = mask.ag;
        this.bankCcMask = mask.cc;
      } else {
        this.bankAgMask = '0000';
        this.bankCcMask = '00000000-0';
      }

      this.formRegisterBank.get('bankAg')?.updateValueAndValidity();
      this.formRegisterBank.get('bankCc')?.updateValueAndValidity();
    });

    this.disableDocumentValidatorsIfBankExists();
  }

  ngAfterViewInit() {
    this.updateDocumentValidators();
  }

  private updateDocumentValidators(): void {
    const selfieControl = this.formRegisterBank.get('selfieFile');
    const idControl = this.formRegisterBank.get('documentFile');

    if (['approved', 'processing', 'pending_manual_analysis'].includes(this.documentsStatus.selfie || '')) {
      selfieControl?.clearValidators();
      selfieControl?.updateValueAndValidity({ emitEvent: false });
    } else {
      selfieControl?.setValidators([Validators.required]);
      selfieControl?.updateValueAndValidity({ emitEvent: false });
    }

    if (['approved', 'processing', 'pending_manual_analysis'].includes(this.documentsStatus.identification || '')) {
      idControl?.clearValidators();
      idControl?.updateValueAndValidity({ emitEvent: false });
    } else {
      idControl?.setValidators([Validators.required]);
      idControl?.updateValueAndValidity({ emitEvent: false });
    }
  }

  updateBankMask(bankCode: string) {
    if (bankCode === '001') {
      this.bankCcMask = 'AAAAAAAAA';
      this.inputPatterns = {
        'A': { pattern: new RegExp('[0-9Xx]') }
      };
    } else {
      this.bankCcMask = '00000000-0';
      this.inputPatterns = {
        '0': { pattern: new RegExp('[0-9]') }
      };
    }
  }


  configureEstimatedRevenueValidation() {
    const control = this.formRegisterBank.get('estimatedRevenue');

    if (this.typeUser === 'PJ' || this.typeUser === 'PF') {
      control?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      control?.clearValidators();
      control?.setValue(null);
    }

    control?.updateValueAndValidity();
  }

  onBlurBankCc(): void {
    const control = this.formRegisterBank.get('bankCc');
    const fullBankValue = this.formRegisterBank.get('bank')?.value;
    const bankCode = fullBankValue?.match(/\d{3}/)?.[0];

    if (!control || !bankCode) return;

    let value = control.value?.toString().replace(/[^a-zA-Z0-9]/g, '') || '';

    const requiredLength = BANK_ACCOUNT_LENGTHS[bankCode];
    if (!requiredLength || value.length < 2) return;

    let digit = value.slice(-1).toUpperCase();
    let number = value.slice(0, -1).replace(/[^0-9]/g, '').padStart(requiredLength, '0');

    if (bankCode === '001') {
      number = number.replace(/[^0-9]/g, '');

      if (!/^[0-9X]$/.test(digit)) return;
    } else {
      digit = digit.replace(/[^0-9]/g, '');
      if (digit.length === 0) return;
    }

    const formattedValue = `${number}-${digit}`;
    control.setValue(formattedValue, { emitEvent: false });
  }

  getDocumentStatusText(status: string): string {
    switch (status) {
      case 'approved':
        return 'Aprovado';
      case 'requested':
        return 'Reenvio solicitado';
      case 'processing':
      case 'pending_manual_analysis':
        return 'Em análise';
      case 'rejected':
        return 'Rejeitado';
      default:
        return 'Não enviado';
    }
  }

  getDocumentStatusClass(status: string): string {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'requested':
      case 'rejected':
        return 'status-rejected';
      case 'processing':
      case 'pending_manual_analysis':
        return 'status-pending';
      default:
        return 'status-empty';
    }
  }

  canUpload(status: string): boolean {
    return !status || status === 'requested' || status === 'rejected';
  }

  private disableDocumentValidatorsIfBankExists(): void {
    const hasBank = this.bankData && this.bankData.length > 0 && this.bankData[0].bankId;

    if (hasBank) {
      const selfie = this.formRegisterBank.get('selfieFile');
      const document = this.formRegisterBank.get('documentFile');

      selfie?.clearValidators();
      document?.clearValidators();

      selfie?.setValue('placeholder');
      document?.setValue('placeholder');

      selfie?.updateValueAndValidity({ emitEvent: false });
      document?.updateValueAndValidity({ emitEvent: false });
    }
  }


  isFormReadyToSave(): boolean {
    const hasBank = this.bankData && this.bankData.length > 0 && this.bankData[0].bankId;

    if (hasBank) {
      return (
        this.formRegisterBank.get('bank')?.valid &&
        this.formRegisterBank.get('bankAg')?.valid &&
        this.formRegisterBank.get('bankCc')?.valid &&
        this.formRegisterBank.get('accountType')?.valid
      );
    }

    const selfieStatus = this.documentsStatus.selfie;
    const idStatus = this.documentsStatus.identification;

    const selfieReady =
      !!this.preview.selfieFile ||
      selfieStatus === 'approved' ||
      selfieStatus === 'processing';

    const documentReady =
      !!this.preview.documentFile ||
      idStatus === 'approved' ||
      idStatus === 'processing';

    const isNewBank = !selfieStatus && !idStatus;

    if (isNewBank) {
      return !!this.preview.selfieFile && !!this.preview.documentFile;
    }

    return selfieReady && documentReady;
  }




  setFormGroup() {
    const bankData = this.bankData ? this.bankData[0] : null;
    const dados = {
      accessId: JSON.parse(localStorage.getItem("user"))?.userToken?.id || 0,
      bank: bankData ? `${bankData.bankCode} - ${bankData.bank}` : null,
      bankAg: bankData ? bankData.bankAg : null,
      accountType: bankData ? String(bankData.accountType) : null,
      bankCc: bankData ? bankData.bankCc : null,
    };

    if (bankData) {
      const mask = BANK_MASKS[bankData.bankCode];
      if (mask) {
        this.bankAgMask = mask.ag;
        this.bankCcMask = mask.cc;
      }
    }

    this.formRegisterBank = this.formBuilder.group({
      accessId: [dados.accessId],
      bank: [dados.bank, Validators.required],
      bankAg: [dados.bankAg, [Validators.required]],
      accountType: [dados.accountType, [Validators.required]],
      bankCc: [dados.bankCc, Validators.required],
      documentFile: [null, Validators.required],
      selfieFile: [null, Validators.required],
      estimatedRevenue: [null],
      isPoliticallyExposedPerson: [false],
    });
  }

  parseCurrency(value: string): number {
    if (!value) return 0;
    return parseFloat(
      value.replace(/\s/g, '').replace('R$', '').replace(/\./g, '').replace(',', '.')
    );
  }

  setFormGroupMessageCheckbox() {
    this.formMessageCheckbox = this.formBuilder.group({
      message: [null],
    });
  }

  formSubmitted = false;
  maxFileSizeMB = 5;
  preview = {
    documentFile: null as { fileName: string; url: string; path: string } | null,
    selfieFile: null as { fileName: string; url: string; path: string } | null,
  };
  loadingUpload = {
    documentFile: false,
    selfieFile: false,
  };

  async onFileChange(event: any, controlName: 'documentFile' | 'selfieFile') {
    const file: File = event.target.files[0];
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const isImage = ['image/jpeg', 'image/png'].includes(file?.type);
    const isPdf = file?.type === 'application/pdf';
    const isValidSize = file?.size <= this.maxFileSizeMB * 1024 * 1024;
    if (!file) return;

    if (!validTypes.includes(file.type)) {
      this.formRegisterBank.get(controlName)?.setErrors({ invalidType: true });
      return;
    }

    if (!isValidSize) {
      this.formRegisterBank.get(controlName)?.setErrors({ maxSize: true });
      return;
    }

    this.loadingUpload[controlName] = true;

    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const category = controlName === 'documentFile' ? 0 : 1;
      const result = await this.uploadFileToIugu(file, category, userData.userToken.id);
      if (result?.url) {
        this.formRegisterBank.patchValue({ [controlName]: file });
        this.formRegisterBank.get(controlName)?.updateValueAndValidity();

        this.preview[controlName] = {
          fileName: file.name,
          url: isPdf ? 'assets/icons/pdf-icon.svg' : '',
          path: result.path || '',
        };

        if (isImage) {
          const reader = new FileReader();
          reader.onload = () => {
            this.preview[controlName].url = reader.result as string;
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (err) {
      console.error('Falha no upload automático:', err);
      this.formRegisterBank.get(controlName)?.setErrors({ uploadFailed: true });
      const backendMessage = err?.error?.message || 'Erro desconhecido';
      const backendDetails = err?.error?.details || '';
      Swal.fire({
        ...customError,
        html: errorHtml(backendMessage, backendDetails),
      });
    } finally {
      this.loadingUpload[controlName] = false;
    }
  }

  async removeFile(controlName: 'documentFile' | 'selfieFile') {
    const file = this.preview[controlName];

    if (file?.path) {
      try {
        const result = await this.fileUploadService.removeFileFromS3(file.path).toPromise();
        if (!result?.success) {
          throw new Error('Não foi possível remover o arquivo do servidor');
        }
      } catch (err) {
        console.error('Erro ao remover arquivo do S3:', err);
        const backendMessage = err?.error?.message || 'Erro ao remover arquivo';
        const backendDetails = err?.error?.details || '';
        Swal.fire({
          ...customError,
          html: errorHtml(backendMessage, backendDetails),
        });
        return;
      }
    }

    this.formRegisterBank.patchValue({ [controlName]: null });
    this.formRegisterBank.get(controlName)?.setErrors({ required: true });
    this.preview[controlName] = null;

    if (controlName === 'documentFile') {
      this.uploadDocumentInput.nativeElement.value = '';
    } else {
      this.uploadSelfieInput.nativeElement.value = '';
    }
  }


  async uploadFileToIugu(file: File, category: number, accessId: number) {
    try {
      const result = await this.fileUploadService
        .uploadIuguFile(file, category, accessId)
        .toPromise();
      return result;
    } catch (err) {
      console.error('Erro ao enviar arquivo Iugu:', err);
      throw err;
    }
  }


  async registerBank(form) {
    this.formSubmitted = true;
    if (form.invalid) return;


    const formData = { ...form.value };

    if (this.typeUser !== 'PJ' && this.typeUser !== 'PF') {
      delete formData.estimatedRevenue;
    }

    const bankCode = formData.bank?.split(' - ')[0];
    const agMask = BANK_MASKS[bankCode]?.ag;
    if (agMask && agMask.includes('-')) {
      const agSemMascara = formData.bankAg?.replace(/\D/g, '');
      const agSemDigito = agSemMascara?.slice(0, -1);
      const digito = agSemMascara?.slice(-1);
      formData.bankAg = `${agSemDigito}-${digito}`;
    }

    const bankId = this.bankData.length > 0 ? this.bankData[0].bankId : 0;

    this.loading = true;
    this.lotsRegisterService.registerBank(formData, bankId).subscribe(
      () => {
        const title = "Dados bancários cadastrados";
        const description = this.isTextModalValid(
          this.modalData.descriptionText
        )
          ? this.modalData.descriptionText
          : this.textModalSuccess;
        const customCss = {
          confirmButtonText: "Continuar",
          customClass: {
            confirmButton: "c-sweetCustom_btnConfirm",
          },
        };
        this.loading = false;
        this.dialogRef.close();
        Swal.fire({ ...customCss, html: successHtml(title, description) });
      },
      (resultError) => {
        this.loading = false;
        let status = resultError.status;
        let title = `Erro ${status}: ${status === 400 ? "Requisição inválida" : "Servidor com problema"}`;
        let errorMessage = resultError?.error || 'Por favor, tente novamente mais tarde.';
        if (resultError?.error?.message) {
          errorMessage = resultError.error.message;
        } else if (resultError?.error?.errors) {
          const allMessages = Object.values(resultError.error.errors)
            .map((errArray: any) => Array.isArray(errArray) ? errArray.join("<br>") : errArray)
            .join("<br>");
          if (allMessages) errorMessage = allMessages;

        }
        Swal.fire({
          ...customError,
          html: errorHtml(title, errorMessage),
        });
      }
    );
  }

  isTextModalValid(text) {
    return typeof text === "string" && text;
  }

  handleBankSearch(value) {
    if (!value.length) {
      this.codeBankList = [];
      return;
    }
    this.codeBankList = this.codeBankListFull.filter((item) => {
      return (
        item.name.toLowerCase().includes(String(value).toLowerCase()) ||
        item.code.toLowerCase().includes(String(value).toLowerCase())
      );
    });
  }

  isInputRadioValid(form: FormGroup) {
    // console.log('form', form.);
    // formRegisterBank.get('accountType').hasError('required')
  }

  returnPage() {
    this.close();
    return this.router.navigate([this.urlBackButton]);
  }

  close() {
    this.dialogRef.close();
  }
}
